/* eslint-disable camelcase */
import Joi from 'joi';
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Form } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useDropdownsQuery } from '../../../redux/dropdowns';
import { useCreateFlexProfessionalMutation } from '../../../redux/managementCompanyWorkforce';
import { showNotification } from '../../../redux/notification';
import { setPreventNavigation, setLeaveConfirmationCopy } from '../../../redux/navigate';
import { user } from '../../../redux/user';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import usePhoneNumberInput from '../../../hooks/usePhoneNumberInput';
import ConfirmationModalNew from '../../../components/modals/ConfirmationModalNew';
import Select from '../../../components/form/Select';
import utils from '../../../utils/utils';
import { ERROR_CODES, PERMISSION, PRIVATE_PATH } from '../../../constants';
import MESSAGE_MAP from '../../../constants/FlexProfessionalMessagesMap';
import warningIcon from '../../../assets/images/warning-icon-2.svg';

import '../../../components/professionals/ProfessionalsEditEmployee.scss';

const { FLEX_PROFESSIONAL_ERROR_CODES } = ERROR_CODES;

const PROFESSIONAL_STATUS_OPTIONS = [
	{ value: 'PENDING', label: 'Pending' },
	{ value: 'ENABLED', label: 'Enabled' },
	{ value: 'DISABLED', label: 'Disabled' }
];

const defaultFormValues = {
	first_name: '',
	last_name: '',
	employee_status: '',
	address: '',
	state_code: '',
	city_name: '',
	zip_code: '',
	phone: '',
	email: ''
};

const validationSchema = Joi.object({
	first_name: Joi.string()
		.max(50)
		.trim()
		.pattern(/^[a-zA-Z -]+$/)
		.required()
		.messages({
			'string.empty': 'First Name is required',
			'any.required': 'First Name is required',
			'string.max': 'First Name can have a maximum length of 50 characters',
			'string.pattern.base': 'First Name is not valid'
		}),
	last_name: Joi.string()
		.max(50)
		.trim()
		.pattern(/^[a-zA-Z -]+$/)
		.required()
		.messages({
			'string.empty': 'Last Name is required',
			'any.required': 'Last Name is required',
			'string.max': 'Last Name can have a maximum length of 50 characters',
			'string.pattern.base': 'Last Name is not valid'
		}),
	employee_status: Joi.string().valid('PENDING', 'ENABLED', 'DISABLED').required().messages({
		'string.empty': 'Professional status is required',
		'any.required': 'Professional status is required',
		'any.only': 'Professional status is required'
	}),
	address: Joi.string().max(255).trim().allow('').optional(),
	state_code: Joi.string().allow('').optional(),
	city_name: Joi.string().trim().allow('').optional(),
	zip_code: Joi.string()
		.length(5)
		.pattern(/^[0-9]{5}$/)
		.trim()
		.required()
		.messages({
			'string.base': 'Zip Code is required',
			'string.empty': 'Zip Code is required',
			'string.length': 'Zip Code is not valid',
			'any.required': 'Zip Code is required',
			'string.pattern.base': 'Zip code is not valid'
		}),
	phone: Joi.string()
		.length(12)
		.pattern(/^[0-9-]+$/)
		.required()
		.messages({
			'string.base': 'Cell Phone number is required',
			'string.empty': 'Cell Phone number is required',
			'any.required': 'Cell Phone number is required',
			'string.length': 'Cell Phone number should be 10 digits long',
			'string.pattern.base': 'Cell Phone number is not valid'
		}),
	email: Joi.string()
		.max(129)
		.trim()
		.email({ tlds: { allow: false } })
		.required()
		.messages({
			'string.base': 'Email is required',
			'string.empty': 'Email is required',
			'any.required': 'Email is required',
			'string.email': 'Email format is invalid',
			'string.max': 'Email can have a maximum length of 129 characters'
		})
});

const validateForm = (formData) => {
	const { error } = validationSchema.validate(formData, { abortEarly: false });
	const formErrors = error?.details.reduce((acc, curr) => {
		acc[curr.context.key] = curr.message;
		return acc;
	}, {});
	return formErrors;
};

const FlexProfessionalsEditEmployee = ({ managementCompanyId }) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const userData = useSelector(user, shallowEqual);

	const prefillData = location.state?.data || {};

	const [formData, setFormData] = useState({
		...defaultFormValues,
		...prefillData,
		phone: prefillData.phone || prefillData.phone_number || ''
	});
	const [formErrors, setFormErrors] = useState(defaultFormValues);
	const [showErrorBanner, setShowErrorBanner] = useState(false);
	const [errorBannerMessage, setErrorBannerMessage] = useState('');
	const [errorBannerCode, setErrorBannerCode] = useState('');
	const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
	const blockedPathRef = useRef('');

	const { isLoading: isStatesLoading, data: states } = useDropdownsQuery('states');
	const [createFlexProfessional, { isLoading: isSaving }] = useCreateFlexProfessionalMutation();

	useEffect(() => {
		dispatch(
			setLeaveConfirmationCopy({
				title: 'Leave without adding professional?',
				message: 'This professional will not be added if you leave now.',
				confirmLabel: 'Leave anyway',
				cancelLabel: 'Stay on page'
			})
		);
		dispatch(setPreventNavigation(true));
		return () => {
			dispatch(setPreventNavigation(false));
			dispatch(setLeaveConfirmationCopy(null));
		};
	}, [dispatch]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSelectChange = (value) => {
		setFormData({ ...formData, ...value });
	};

	const updatePhoneNumber = usePhoneNumberInput((name, value) => setFormData({ ...formData, [name]: value }));

	const handleCancel = () => {
		blockedPathRef.current = PRIVATE_PATH.WORKFORCE_PAGE;
		setShowLeaveConfirmation(true);
	};

	const handleConfirmLeave = () => {
		dispatch(setPreventNavigation(false));
		setShowLeaveConfirmation(false);
		setTimeout(() => {
			history.push(blockedPathRef.current || PRIVATE_PATH.WORKFORCE_PAGE);
		}, 100);
	};

	const handleStayOnPage = () => {
		setShowLeaveConfirmation(false);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setShowErrorBanner(false);

		const errors = validateForm(formData);
		setFormErrors((prevErrors) => ({ ...prevErrors, ...defaultFormValues, ...errors }));

		if (errors) return;

		const trimmedFormData = Object.fromEntries(
			Object.entries(formData).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
		);

		try {
			const result = await createFlexProfessional({ managementCompanyId, body: trimmedFormData }).unwrap();
			if (result && result.isSuccessful) {
				dispatch(setPreventNavigation(false));
				const canModifyCredentials = utils.isAuthorizedExact(
					PERMISSION.WORKFORCE_CREDENTIALS_EDIT,
					userData.managementCompanyPermissions[managementCompanyId]
				);
				const nextStepPath = canModifyCredentials ? 'credentials' : 'employee-details';
				setTimeout(() => {
					history.replace(
						`${PRIVATE_PATH.WORKFORCE_PAGE}/${managementCompanyId}/professionals/${result.data.caregiver_id}/${nextStepPath}`
					);
				}, 100);
			}
		} catch (error) {
			const currentError = error?.data?.errors?.[0];
			if (!currentError) {
				dispatch(showNotification('error', 'Something went wrong. Please try again.'));
				return;
			}

			if (currentError.field) {
				setFormErrors((prevErrors) => ({
					...prevErrors,
					...defaultFormValues,
					[currentError.field]: currentError.message
				}));
			}

			if (
				currentError.code === FLEX_PROFESSIONAL_ERROR_CODES.CONTACT_US ||
				currentError.code === FLEX_PROFESSIONAL_ERROR_CODES.ALREADY_EXISTS
			) {
				setErrorBannerMessage(MESSAGE_MAP[currentError.code]);
				setErrorBannerCode(currentError.code);
				setShowErrorBanner(true);
			} else if (currentError.message) {
				dispatch(showNotification('error', currentError.message));
			}
		}
	};

	if (isStatesLoading) {
		return (
			<div className="spinner-center-wrapper" key="spinner">
				<LoadingSpinner className="saving-spinner" />
			</div>
		);
	}

	return (
		<WithOnlyOnDesktop>
			<div className="employee-details__wrapper flex-workforce">
				<Form onSubmit={handleSubmit}>
					<div className="employee-details">
						<h2>Name</h2>
						<Form.Group controlId="first_name" className="form-input-group">
							<Form.Label>First name</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								name="first_name"
								placeholder="First name"
								className="form-input"
								onChange={handleChange}
								value={formData.first_name}
								isInvalid={formErrors.first_name}
							/>
							<Form.Control.Feedback type="invalid">{formErrors.first_name}</Form.Control.Feedback>
						</Form.Group>
						<Form.Group controlId="last_name" className="form-input-group">
							<Form.Label>Last name</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								name="last_name"
								placeholder="Last name"
								className="form-input"
								onChange={handleChange}
								value={formData.last_name}
								isInvalid={formErrors.last_name}
							/>
							<Form.Control.Feedback type="invalid">{formErrors.last_name}</Form.Control.Feedback>
						</Form.Group>
						<Form.Group controlId="employee_status" className="form-input-group">
							<Form.Label>Professional status</Form.Label>
							<Select
								className="select employee-edit-page"
								name="employee_status"
								options={PROFESSIONAL_STATUS_OPTIONS}
								value={formData.employee_status}
								handleChange={handleSelectChange}
								placeholder="Select..."
							/>
							{formErrors.employee_status && (
								<div className="invalid-feedback-custom">{formErrors.employee_status}</div>
							)}
						</Form.Group>
					</div>
					<div className="employee-details">
						<h2>Home Address</h2>
						<Form.Group controlId="address" className="form-input-group">
							<Form.Label>
								Street address <span className="optional-label">(Optional)</span>
							</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								className="form-input"
								name="address"
								placeholder="Street address"
								onChange={handleChange}
								value={formData.address}
								isInvalid={formErrors.address}
							/>
							<Form.Control.Feedback type="invalid">{formErrors.address}</Form.Control.Feedback>
						</Form.Group>
						<div className="input-group">
							<Form.Group controlId="city_name" className="form-input-group location-input-group">
								<Form.Label>
									City <span className="optional-label">(Optional)</span>
								</Form.Label>
								<Form.Control
									size="lg"
									type="text"
									className="form-input"
									name="city_name"
									placeholder="City"
									onChange={handleChange}
									value={formData.city_name}
									isInvalid={formErrors.city_name}
								/>
								<Form.Control.Feedback type="invalid">{formErrors.city_name}</Form.Control.Feedback>
							</Form.Group>
							<Form.Group controlId="state_code" className="form-input-group location-input-group">
								<Form.Label className="form-label">
									State <span className="optional-label">(Optional)</span>
								</Form.Label>
								<Select
									className="select employee-edit-page"
									name="state_code"
									options={states}
									value={formData.state_code}
									handleChange={handleSelectChange}
									placeholder="Select..."
									isSearchable
								/>
								{formErrors.state_code && <div className="invalid-feedback-custom">{formErrors.state_code}</div>}
							</Form.Group>
							<Form.Group controlId="zip_code" className="form-input-group location-input-group">
								<Form.Label>ZIP</Form.Label>
								<Form.Control
									size="lg"
									type="text"
									className="form-input"
									name="zip_code"
									placeholder="ZIP"
									onChange={handleChange}
									value={formData.zip_code}
									isInvalid={formErrors.zip_code}
								/>
								<Form.Control.Feedback type="invalid">{formErrors.zip_code}</Form.Control.Feedback>
							</Form.Group>
						</div>
					</div>
					<div className="employee-details">
						<h2>Contact Information</h2>
						<Form.Group controlId="phone" className="form-input-group">
							<Form.Label>Cell phone</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								className="form-input"
								name="phone"
								placeholder="555-555-5555"
								onChange={updatePhoneNumber}
								value={formData.phone}
								isInvalid={formErrors.phone}
							/>
							<Form.Control.Feedback type="invalid">{formErrors.phone}</Form.Control.Feedback>
						</Form.Group>
						<Form.Group controlId="email" className="form-input-group">
							<Form.Label>Email</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								className="form-input"
								name="email"
								placeholder="email@address.com"
								onChange={handleChange}
								value={formData.email}
								isInvalid={formErrors.email}
							/>
							<Form.Control.Feedback type="invalid">{formErrors.email}</Form.Control.Feedback>
						</Form.Group>
						{showErrorBanner && (
							<div
								className={classNames('notification-banner-error', 'flex-workforce', {
									'already-exists': errorBannerCode === FLEX_PROFESSIONAL_ERROR_CODES.ALREADY_EXISTS
								})}
							>
								<img className="notification-banner-icon" alt="Warning icon" src={warningIcon} />
								<span className="notification-banner-message">{errorBannerMessage}</span>
							</div>
						)}
					</div>
				</Form>
				<div className="form-actions">
					<button type="button" className="form-actions__button secondary-button" onClick={handleCancel}>
						Cancel
					</button>
					<button
						type="submit"
						className="form-actions__button primary-button"
						onClick={handleSubmit}
						disabled={isSaving}
					>
						Add professional
					</button>
				</div>
			</div>
			{showLeaveConfirmation && (
				<ConfirmationModalNew
					title="Leave without adding professional?"
					onConfirm={handleConfirmLeave}
					confirmLabel="Leave anyway"
					onCancel={handleStayOnPage}
					cancelLabel="Stay on page"
				>
					This professional will not be added if you leave now.
				</ConfirmationModalNew>
			)}
		</WithOnlyOnDesktop>
	);
};

export default FlexProfessionalsEditEmployee;
